import get from 'lodash/get';

import { Telemetry } from './telemetry';

/**
 * A function that takes an `update` function used by our Controller pattern and returns a function with the same calling
 * conventions, but that logs the new model passed to the update function to our telemetry system; this is restricted to the
 * properties supplied in propNames.
 *
 * This function may also be used standalone by ignoring the third parameter and just invoking the returned function
 * with a new model.
 *
 * This function is heavy. Do not use it for models that frequently change. Be very careful NOT to log PII via this function.
 *
 * Example usage:
 * -  this.update = telemetryModelUpdater(AiController.name, ['modalOpen', 'sessionId'], this.update).bind(this);
 *
 * @param componentName User supplied name of the component to be logged.
 * @param propNames A list of property names that will be extracted from the model and sent to the server, or a single array item with a wildcard for every property.
 * @param update The update function which the controller normally provides.
 */
export function telemetryModelUpdateWrapper<T>(
  componentName: string,
  propNames: string[],
  update?: (model: T) => void
): (delta: T) => void {
  const cache: Record<string, unknown> = {};
  const checkAllProps = propNames.includes('*');

  return (delta) => {
    const change: Record<string, unknown> = {};

    if (!delta) {
      update?.(delta);
      return;
    }

    // can't do anything if it's not an object; we could logically do something with this,
    // but right now it's not supported.
    if (checkAllProps && typeof delta !== 'object') {
      update?.(delta);
      return;
    }

    const localNames = checkAllProps ? Object.keys(delta) : propNames;
    for (const path of localNames) {
      const value = get(delta, path) as unknown;
      if (cache[path] === value) {
        continue;
      }

      cache[path] = value;
      change[path] = value;
    }

    if (Object.keys(change).length === 0) {
      update?.(delta);
      return;
    }

    Telemetry.log({
      eventName: 'controller-update',
      eventSource: componentName,
      eventDescription: `The following keys had values modified: ${Object.keys(change).join()}`,
      eventData: {
        change,
      },
    });

    update?.(delta);
  };
}
